// Popover — minimal floating panel anchored to a trigger element.
//
// Used by the diary row's Highlight action (and reserved for future
// dropdown actions). Implementation goals:
//   - Click-outside dismiss, Escape dismiss
//   - Focus trapped inside the panel while open
//   - Re-positions to the right of the trigger by default; falls back
//     to below when there isn't horizontal room
//   - No external dependencies (we already excluded Floating UI from §2)
//
// Anchored positioning is computed against the trigger ref so the panel
// follows the trigger across scroll. The panel itself is rendered into
// a portal under document.body so overflow:hidden ancestors don't clip
// it (e.g. inside a Table cell).

import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../utils/cn';
import styles from './Popover.module.css';

export interface PopoverProps {
  /** The element that toggles the popover. Must accept a ref + onClick. */
  trigger: ReactElement;
  /** When supplied, the parent owns open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  /** Optional ariaLabel — required when no `<header>` is rendered inside. */
  ariaLabel?: string;
  className?: string;
  placement?: 'right' | 'bottom';
}

interface Position {
  top: number;
  left: number;
}

function computePosition(
  triggerRect: DOMRect,
  panelEl: HTMLElement,
  placement: 'right' | 'bottom',
): Position {
  const margin = 6;
  const panelRect = panelEl.getBoundingClientRect();
  if (placement === 'right') {
    const right = triggerRect.right + margin;
    if (right + panelRect.width <= window.innerWidth) {
      return { top: triggerRect.top, left: right };
    }
    // Not enough room to the right — fall through to bottom.
  }
  return {
    top: triggerRect.bottom + margin,
    left: Math.max(
      8,
      Math.min(window.innerWidth - panelRect.width - 8, triggerRect.left),
    ),
  };
}

export function Popover({
  trigger,
  open: openProp,
  onOpenChange,
  children,
  ariaLabel,
  className,
  placement = 'right',
}: PopoverProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  };

  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Position>({ top: 0, left: 0 });
  const labelId = useId();

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos(computePosition(rect, panelRef.current, placement));
  }, [open, placement]);

  // Click outside dismiss.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Escape dismiss + initial focus.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => panelRef.current?.focus(), 0);
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isValidElement(trigger)) {
    throw new Error('Popover.trigger must be a valid React element');
  }

  // Inject ref + onClick into the trigger.
  const triggerProps = trigger.props as {
    onClick?: (e: React.MouseEvent) => void;
  };
  const triggerWithRef = cloneElement(
    trigger as ReactElement<{
      ref?: RefObject<HTMLElement>;
      onClick?: (e: React.MouseEvent) => void;
      'aria-expanded'?: boolean;
      'aria-haspopup'?: 'menu' | 'dialog';
    }>,
    {
      ref: triggerRef as RefObject<HTMLElement>,
      onClick: (e: React.MouseEvent) => {
        triggerProps.onClick?.(e);
        setOpen(!open);
      },
      'aria-expanded': open,
      'aria-haspopup': 'dialog',
    },
  );

  function panelKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Tab') {
      // Loose focus trap — when Tab leaves the panel we just close it.
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  return (
    <>
      {triggerWithRef}
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              tabIndex={-1}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabel ? undefined : labelId}
              onKeyDown={panelKey}
              className={cn(styles.panel, className)}
              style={{ top: pos.top, left: pos.left }}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
